import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, User, Mail, Phone, Users, Save, Loader2, 
  CheckCircle, Shield, Calendar, Sparkles, Crown, Settings,
  LogOut, Trash2, Edit3, Clock, MapPin, Bell, BellOff, Sun,
  KeyRound, Smartphone, X, QrCode
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/components/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, logout, updateProfile } = useAuth();
  
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    gender: '',
    birthDate: '',
    birthTime: '',
    birthCity: '',
    phone: ''
  });
  
  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState({
    notification_enabled: true,
    notification_email: true,
    notification_push: true,
    notification_hour: 8
  });
  const [notifLoading, setNotifLoading] = useState(false);
  const [isPremium, setIsPremium] = useState(false);

  // 2FA State
  const [twoFAStatus, setTwoFAStatus] = useState({ enabled: false, method: null });
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFAMethod, setTwoFAMethod] = useState('email');
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [twoFASetupData, setTwoFASetupData] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAStep, setTwoFAStep] = useState('choose'); // 'choose', 'verify', 'disable'

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.first_name || user.name || '',
        gender: user.gender || '',
        birthDate: user.birth_date || '',
        birthTime: user.birth_time || '',
        birthCity: user.birth_city || '',
        phone: user.phone || ''
      });
      
      // Load notification preferences
      loadNotificationPrefs();
      // Load 2FA status
      load2FAStatus();
    }
  }, [user]);
  
  const load2FAStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/2fa/status`, {
        withCredentials: true
      });
      setTwoFAStatus({
        enabled: response.data.enabled,
        method: response.data.method
      });
    } catch (err) {
      console.error('Error loading 2FA status:', err);
    }
  };
  
  const loadNotificationPrefs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notifications/preferences`, {
        withCredentials: true
      });
      if (response.data.success) {
        setNotifPrefs(response.data.preferences);
        setIsPremium(response.data.is_premium);
      }
    } catch (err) {
      console.error('Error loading notification preferences:', err);
    }
  };
  
  const saveNotificationPrefs = async (newPrefs) => {
    setNotifLoading(true);
    try {
      await axios.put(`${API_URL}/api/notifications/preferences`, newPrefs, {
        withCredentials: true
      });
      setNotifPrefs(newPrefs);
      toast.success('Préférences de notifications mises à jour');
    } catch (err) {
      console.error('Error saving notification preferences:', err);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setNotifLoading(false);
    }
  };
  
  const toggleNotification = (key) => {
    const newPrefs = { ...notifPrefs, [key]: !notifPrefs[key] };
    saveNotificationPrefs(newPrefs);
  };
  
  const updateNotificationHour = (hour) => {
    const newPrefs = { ...notifPrefs, notification_hour: parseInt(hour) };
    saveNotificationPrefs(newPrefs);
  };

  // 2FA Functions
  const start2FASetup = async (method) => {
    setTwoFALoading(true);
    setTwoFAMethod(method);
    try {
      const response = await axios.post(`${API_URL}/api/auth/2fa/setup`, 
        { method },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setTwoFASetupData(response.data);
        setTwoFAStep('verify');
        
        if (method === 'email') {
          toast.success('Code envoyé par email');
        }
      }
    } catch (err) {
      console.error('Error starting 2FA setup:', err);
      toast.error('Erreur lors de la configuration 2FA');
    } finally {
      setTwoFALoading(false);
    }
  };

  const confirm2FASetup = async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      toast.error('Veuillez entrer un code à 6 chiffres');
      return;
    }
    
    setTwoFALoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/2fa/enable`,
        { code: twoFACode, method: twoFAMethod },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        toast.success('Double authentification activée !');
        setTwoFAStatus({ enabled: true, method: twoFAMethod });
        close2FAModal();
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Code invalide';
      toast.error(message);
      setTwoFACode('');
    } finally {
      setTwoFALoading(false);
    }
  };

  const startDisable2FA = async () => {
    setTwoFALoading(true);
    setTwoFAStep('disable');
    
    // If method is email, send a code first
    if (twoFAStatus.method === 'email') {
      try {
        await axios.post(`${API_URL}/api/auth/2fa/setup`,
          { method: 'email' },
          { withCredentials: true }
        );
        toast.success('Code envoyé par email');
      } catch (err) {
        console.error('Error sending disable code:', err);
      }
    }
    setTwoFALoading(false);
  };

  const confirmDisable2FA = async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      toast.error('Veuillez entrer un code à 6 chiffres');
      return;
    }
    
    setTwoFALoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/2fa/disable`,
        { code: twoFACode, method: twoFAStatus.method },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        toast.success('Double authentification désactivée');
        setTwoFAStatus({ enabled: false, method: null });
        close2FAModal();
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Code invalide';
      toast.error(message);
      setTwoFACode('');
    } finally {
      setTwoFALoading(false);
    }
  };

  const close2FAModal = () => {
    setShow2FASetup(false);
    setTwoFAStep('choose');
    setTwoFACode('');
    setTwoFASetupData(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateProfile({
        first_name: formData.firstName,
        gender: formData.gender || null,
        birth_date: formData.birthDate || null,
        birth_time: formData.birthTime || null,
        birth_city: formData.birthCity || null,
        phone: formData.phone || null
      });
      toast.success('Profil mis à jour avec succès !');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Erreur lors de la mise à jour du profil');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
    toast.success('Déconnexion réussie');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const memberSince = user.created_at 
    ? new Date(user.created_at).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'Inconnu';

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50">
      {/* Decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-64 h-64 bg-emerald-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-teal-200/20 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-emerald-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="rounded-full text-gray-600 hover:bg-emerald-50"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-emerald-600" />
              <span className="font-bold text-gray-900">Mon Profil</span>
            </div>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Profile Header Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl border border-emerald-200 shadow-lg overflow-hidden mb-6"
        >
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/30">
                {user.picture ? (
                  <img src={user.picture} alt="Profile" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span className="text-3xl font-bold">
                    {(user.first_name || user.name || 'U')[0].toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold">{user.first_name || user.name}</h1>
                <p className="text-white/80 text-sm">{user.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    user.auth_type === 'google' 
                      ? 'bg-white/20 text-white' 
                      : 'bg-emerald-600 text-white'
                  }`}>
                    {user.auth_type === 'google' ? '🔐 Google' : '📧 Email'}
                  </span>
                  {user.gender && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      user.gender === 'M' ? 'bg-blue-500/30' : 'bg-pink-500/30'
                    }`}>
                      {user.gender === 'M' ? '♂ Masculin' : '♀ Féminin'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-emerald-50/50 border-t border-emerald-100">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="h-4 w-4" />
                <span>Membre depuis {memberSince}</span>
              </div>
              <div className="flex items-center gap-2 text-emerald-600">
                <Shield className="h-4 w-4" />
                <span>Compte vérifié</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Edit Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl border border-emerald-200 shadow-lg p-6 mb-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Settings className="h-5 w-5 text-emerald-600" />
              Informations personnelles
            </h2>
            {!isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-xl"
                data-testid="edit-profile-btn"
              >
                <Edit3 className="h-4 w-4 mr-2" />
                Modifier
              </Button>
            )}
          </div>

          <div className="space-y-5">
            {/* First Name */}
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium flex items-center gap-2">
                <User className="h-4 w-4 text-gray-400" />
                Prénom
              </Label>
              {isEditing ? (
                <Input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400"
                  placeholder="Votre prénom"
                  data-testid="input-firstname"
                />
              ) : (
                <p className="text-gray-900 py-2 px-3 bg-gray-50 rounded-xl">
                  {user.first_name || user.name || '-'}
                </p>
              )}
            </div>

            {/* Email (read-only) */}
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-400" />
                Email
              </Label>
              <p className="text-gray-900 py-2 px-3 bg-gray-50 rounded-xl flex items-center justify-between">
                {user.email}
                <span className="text-xs text-gray-400">(non modifiable)</span>
              </p>
            </div>

            {/* Gender */}
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-400" />
                Genre
              </Label>
              {isEditing ? (
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, gender: 'M' })}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition-all ${
                      formData.gender === 'M' 
                        ? 'border-blue-500 bg-blue-50 text-blue-700' 
                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 text-gray-600'
                    }`}
                    data-testid="gender-m-btn"
                  >
                    <span className="text-lg">♂</span>
                    <span className="font-medium">Masculin</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, gender: 'F' })}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition-all ${
                      formData.gender === 'F' 
                        ? 'border-pink-500 bg-pink-50 text-pink-700' 
                        : 'border-gray-200 hover:border-pink-300 hover:bg-pink-50/50 text-gray-600'
                    }`}
                    data-testid="gender-f-btn"
                  >
                    <span className="text-lg">♀</span>
                    <span className="font-medium">Féminin</span>
                  </button>
                </div>
              ) : (
                <p className="text-gray-900 py-2 px-3 bg-gray-50 rounded-xl">
                  {user.gender === 'M' ? '♂ Masculin' : user.gender === 'F' ? '♀ Féminin' : 'Non renseigné'}
                </p>
              )}
            </div>

            {/* Birth Date Section */}
            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-emerald-700 mb-4 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Informations de naissance (pour ASTROZODIAC & NUMEROLOGIA)
              </h3>
              
              {/* Birth Date */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-700 font-medium flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    Date de naissance
                  </Label>
                  {isEditing ? (
                    <Input
                      type="date"
                      value={formData.birthDate}
                      onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                      className="rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400"
                      data-testid="input-birthdate"
                    />
                  ) : (
                    <p className="text-gray-900 py-2 px-3 bg-gray-50 rounded-xl">
                      {user.birth_date ? new Date(user.birth_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Non renseigné'}
                    </p>
                  )}
                </div>

                {/* Birth Time */}
                <div className="space-y-2">
                  <Label className="text-gray-700 font-medium flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    Heure de naissance <span className="text-xs text-gray-400">(pour l'ascendant)</span>
                  </Label>
                  {isEditing ? (
                    <Input
                      type="time"
                      value={formData.birthTime}
                      onChange={(e) => setFormData({ ...formData, birthTime: e.target.value })}
                      className="rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400"
                      data-testid="input-birthtime"
                    />
                  ) : (
                    <p className="text-gray-900 py-2 px-3 bg-gray-50 rounded-xl">
                      {user.birth_time || 'Non renseigné'}
                    </p>
                  )}
                </div>

                {/* Birth City */}
                <div className="space-y-2">
                  <Label className="text-gray-700 font-medium flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    Ville de naissance
                  </Label>
                  {isEditing ? (
                    <Input
                      type="text"
                      value={formData.birthCity}
                      onChange={(e) => setFormData({ ...formData, birthCity: e.target.value })}
                      className="rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400"
                      placeholder="Paris"
                      data-testid="input-birthcity"
                    />
                  ) : (
                    <p className="text-gray-900 py-2 px-3 bg-gray-50 rounded-xl">
                      {user.birth_city || 'Non renseigné'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className="space-y-2 pt-4 border-t border-gray-100">
              <Label className="text-gray-700 font-medium flex items-center gap-2">
                <Phone className="h-4 w-4 text-gray-400" />
                Téléphone <span className="text-xs text-gray-400">(optionnel)</span>
              </Label>
              {isEditing ? (
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400"
                  placeholder="+33 6 12 34 56 78"
                  data-testid="input-phone"
                />
              ) : (
                <p className="text-gray-900 py-2 px-3 bg-gray-50 rounded-xl">
                  {user.phone || 'Non renseigné'}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            {isEditing && (
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditing(false);
                    setFormData({
                      firstName: user.first_name || user.name || '',
                      gender: user.gender || '',
                      birthDate: user.birth_date || '',
                      birthTime: user.birth_time || '',
                      birthCity: user.birth_city || '',
                      phone: user.phone || ''
                    });
                  }}
                  className="flex-1 rounded-xl border-gray-200 text-gray-600 hover:bg-gray-50"
                  data-testid="cancel-btn"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex-1 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white"
                  data-testid="save-btn"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Enregistrer
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Premium Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl border border-amber-200 p-6 mb-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Crown className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Statut Premium</h3>
                <p className="text-sm text-gray-600">Débloquez toutes les fonctionnalités</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/premium')}
              className="rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white"
              data-testid="premium-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Voir les offres
            </Button>
          </div>
        </motion.div>

        {/* Notification Preferences Card */}
        {isPremium && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-3xl border border-sky-200 shadow-lg p-6 mb-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-cyan-500 flex items-center justify-center">
                <Bell className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Notifications Horoscope</h3>
                <p className="text-sm text-gray-500">Recevez votre horoscope chaque jour</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* Master Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  {notifPrefs.notification_enabled ? (
                    <Bell className="h-5 w-5 text-sky-500" />
                  ) : (
                    <BellOff className="h-5 w-5 text-gray-400" />
                  )}
                  <div>
                    <p className="font-medium text-gray-900">Notifications quotidiennes</p>
                    <p className="text-xs text-gray-500">Rappel pour consulter votre horoscope</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleNotification('notification_enabled')}
                  disabled={notifLoading}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    notifPrefs.notification_enabled ? 'bg-sky-500' : 'bg-gray-300'
                  }`}
                  data-testid="toggle-notifications"
                >
                  <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    notifPrefs.notification_enabled ? 'left-7' : 'left-1'
                  }`} />
                </button>
              </div>
              
              {notifPrefs.notification_enabled && (
                <>
                  {/* Email Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">Notification par email</p>
                        <p className="text-xs text-gray-500">Recevez un email avec l'aperçu</p>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleNotification('notification_email')}
                      disabled={notifLoading}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        notifPrefs.notification_email ? 'bg-sky-500' : 'bg-gray-300'
                      }`}
                      data-testid="toggle-email"
                    >
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        notifPrefs.notification_email ? 'left-7' : 'left-1'
                      }`} />
                    </button>
                  </div>
                  
                  {/* Time Selector */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3 mb-3">
                      <Sun className="h-5 w-5 text-amber-500" />
                      <div>
                        <p className="font-medium text-gray-900">Heure de notification</p>
                        <p className="text-xs text-gray-500">Choisissez votre heure idéale</p>
                      </div>
                    </div>
                    <select
                      value={notifPrefs.notification_hour}
                      onChange={(e) => updateNotificationHour(e.target.value)}
                      disabled={notifLoading}
                      className="w-full p-3 border border-gray-200 rounded-xl bg-white focus:border-sky-400 focus:ring-1 focus:ring-sky-400 outline-none"
                      data-testid="notification-hour"
                    >
                      {[...Array(24)].map((_, i) => (
                        <option key={i} value={i}>
                          {i.toString().padStart(2, '0')}:00 {i < 12 ? '(matin)' : i < 18 ? '(après-midi)' : '(soir)'}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}

        {/* Two-Factor Authentication Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.27 }}
          className="bg-white rounded-3xl border border-violet-200 shadow-lg p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900">Double authentification (2FA)</h3>
              <p className="text-sm text-gray-500">Sécurisez votre compte avec un code de vérification</p>
            </div>
            {twoFAStatus.enabled && (
              <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                Activé
              </span>
            )}
          </div>
          
          {twoFAStatus.enabled ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-violet-50 rounded-xl">
                <div className="flex items-center gap-3">
                  {twoFAStatus.method === 'email' ? (
                    <Mail className="h-5 w-5 text-violet-500" />
                  ) : (
                    <Smartphone className="h-5 w-5 text-violet-500" />
                  )}
                  <div>
                    <p className="font-medium text-gray-900">
                      {twoFAStatus.method === 'email' ? 'Vérification par email' : 'Application d\'authentification'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {twoFAStatus.method === 'email' 
                        ? 'Un code est envoyé à votre email à chaque connexion'
                        : 'Utilisez Google Authenticator ou similaire'}
                    </p>
                  </div>
                </div>
              </div>
              
              <Button
                variant="outline"
                onClick={() => {
                  setShow2FASetup(true);
                  startDisable2FA();
                }}
                className="w-full border-red-200 text-red-600 hover:bg-red-50 rounded-xl"
                data-testid="disable-2fa-btn"
              >
                Désactiver la 2FA
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 bg-amber-50 p-3 rounded-xl border border-amber-200">
                La double authentification ajoute une couche de sécurité supplémentaire à votre compte en demandant un code de vérification lors de chaque connexion.
              </p>
              
              <Button
                onClick={() => setShow2FASetup(true)}
                className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500 text-white rounded-xl"
                data-testid="enable-2fa-btn"
              >
                <Shield className="h-4 w-4 mr-2" />
                Activer la 2FA
              </Button>
            </div>
          )}
        </motion.div>

        {/* 2FA Setup Modal */}
        <AnimatePresence>
          {show2FASetup && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white w-full max-w-md rounded-3xl border border-violet-200 shadow-2xl p-6 max-h-[90vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-gray-900">
                    {twoFAStep === 'disable' ? 'Désactiver la 2FA' : 'Configurer la 2FA'}
                  </h3>
                  <button
                    onClick={close2FAModal}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    data-testid="close-2fa-modal-btn"
                  >
                    <X className="h-5 w-5 text-gray-500" />
                  </button>
                </div>

                {/* Step: Choose Method */}
                {twoFAStep === 'choose' && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 mb-4">
                      Choisissez comment recevoir vos codes de vérification :
                    </p>
                    
                    <button
                      onClick={() => start2FASetup('email')}
                      disabled={twoFALoading}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-violet-400 hover:bg-violet-50 transition-all text-left flex items-center gap-4"
                      data-testid="2fa-email-option"
                    >
                      <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                        <Mail className="h-6 w-6 text-violet-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Par email</p>
                        <p className="text-sm text-gray-500">Recevez un code à votre adresse email</p>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => start2FASetup('totp')}
                      disabled={twoFALoading}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-violet-400 hover:bg-violet-50 transition-all text-left flex items-center gap-4"
                      data-testid="2fa-totp-option"
                    >
                      <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                        <Smartphone className="h-6 w-6 text-violet-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Application d'authentification</p>
                        <p className="text-sm text-gray-500">Google Authenticator, Authy, etc.</p>
                      </div>
                    </button>
                    
                    {twoFALoading && (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
                      </div>
                    )}
                  </div>
                )}

                {/* Step: Verify Code */}
                {twoFAStep === 'verify' && (
                  <div className="space-y-4">
                    {twoFAMethod === 'totp' && twoFASetupData?.qr_code && (
                      <div className="text-center space-y-4">
                        <p className="text-sm text-gray-600">
                          Scannez ce QR code avec votre application d'authentification :
                        </p>
                        <div className="flex justify-center">
                          <img 
                            src={twoFASetupData.qr_code} 
                            alt="QR Code" 
                            className="w-48 h-48 border rounded-xl"
                            data-testid="2fa-qr-code"
                          />
                        </div>
                        <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-xl break-all">
                          <p className="font-medium mb-1">Ou entrez ce code manuellement :</p>
                          <code className="font-mono">{twoFASetupData.secret}</code>
                        </div>
                      </div>
                    )}
                    
                    {twoFAMethod === 'email' && (
                      <p className="text-sm text-gray-600 text-center">
                        Un code de vérification a été envoyé à votre adresse email.
                      </p>
                    )}
                    
                    <div className="space-y-2">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <KeyRound className="h-4 w-4 text-gray-400" />
                        Code de vérification
                      </Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        placeholder="000000"
                        value={twoFACode}
                        onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="text-center text-2xl font-mono tracking-[0.5em] py-4 rounded-xl"
                        data-testid="2fa-setup-code-input"
                      />
                    </div>
                    
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() => setTwoFAStep('choose')}
                        className="flex-1 rounded-xl"
                        data-testid="2fa-back-btn"
                      >
                        Retour
                      </Button>
                      <Button
                        onClick={confirm2FASetup}
                        disabled={twoFALoading || twoFACode.length !== 6}
                        className="flex-1 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500 text-white rounded-xl"
                        data-testid="2fa-confirm-btn"
                      >
                        {twoFALoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Activer'
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step: Disable */}
                {twoFAStep === 'disable' && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 text-center">
                      {twoFAStatus.method === 'email'
                        ? 'Entrez le code reçu par email pour désactiver la 2FA'
                        : 'Entrez le code de votre application d\'authentification pour désactiver la 2FA'}
                    </p>
                    
                    <div className="space-y-2">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <KeyRound className="h-4 w-4 text-gray-400" />
                        Code de vérification
                      </Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        placeholder="000000"
                        value={twoFACode}
                        onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="text-center text-2xl font-mono tracking-[0.5em] py-4 rounded-xl"
                        data-testid="2fa-disable-code-input"
                      />
                    </div>
                    
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={close2FAModal}
                        className="flex-1 rounded-xl"
                      >
                        Annuler
                      </Button>
                      <Button
                        onClick={confirmDisable2FA}
                        disabled={twoFALoading || twoFACode.length !== 6}
                        className="flex-1 bg-red-500 hover:bg-red-400 text-white rounded-xl"
                        data-testid="2fa-confirm-disable-btn"
                      >
                        {twoFALoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Désactiver'
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-2 gap-4"
        >
          <button
            onClick={() => navigate('/home')}
            className="p-4 bg-white rounded-2xl border border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all text-left"
            data-testid="go-lumina-btn"
          >
            <span className="text-2xl mb-2 block">🖐️</span>
            <span className="font-semibold text-gray-900 block">LUMINA</span>
            <span className="text-xs text-gray-500">Lecture de paume</span>
          </button>
          
          <button
            onClick={() => navigate('/mysteriae')}
            className="p-4 bg-white rounded-2xl border border-purple-200 hover:border-purple-400 hover:bg-purple-50 transition-all text-left"
            data-testid="go-mysteriae-btn"
          >
            <span className="text-2xl mb-2 block">🔮</span>
            <span className="font-semibold text-gray-900 block">MYSTERIAE</span>
            <span className="text-xs text-gray-500">Tarot</span>
          </button>
          
          <button
            onClick={() => navigate('/astrozodiac')}
            className="p-4 bg-white rounded-2xl border border-sky-200 hover:border-sky-400 hover:bg-sky-50 transition-all text-left"
            data-testid="go-astrozodiac-btn"
          >
            <span className="text-2xl mb-2 block">⭐</span>
            <span className="font-semibold text-gray-900 block">ASTROZODIAC</span>
            <span className="text-xs text-gray-500">Astrologie</span>
          </button>
          
          <button
            onClick={() => navigate('/numerologia')}
            className="p-4 bg-white rounded-2xl border border-amber-200 hover:border-amber-400 hover:bg-amber-50 transition-all text-left"
            data-testid="go-numerologia-btn"
          >
            <span className="text-2xl mb-2 block">🔢</span>
            <span className="font-semibold text-gray-900 block">NUMEROLOGIA</span>
            <span className="text-xs text-gray-500">Numérologie</span>
          </button>
        </motion.div>
      </main>
    </div>
  );
};

export default ProfilePage;
